import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

/**
 * HostGuard — production-domain enforcer.
 *
 * Any request served from a hostname OTHER than `thepropertic.ae`
 * (e.g. `dubai-market-intel.emergent.host`, `*.preview.emergentagent.com`,
 * localhost, staging clones) MUST emit an aggressive `noindex, nofollow`
 * meta block across every major crawler AND rewrite the canonical to the
 * production origin, so any accidentally-indexed non-prod URL is either
 * dropped or consolidated at the search-engine side.
 *
 * Why in JS rather than robots.txt: our static `public/robots.txt` is
 * shipped inside the same build to every host, so it cannot differ per
 * host without a dynamic backend rewrite. Meta-robots + canonical is
 * evaluated by every JS-rendering crawler (Googlebot, Bingbot, Yandex,
 * ClaudeBot, GPTBot, PerplexityBot, etc.) after paint.
 *
 * Rendered LAST inside <HelmetProvider> so react-helmet-async's
 * last-write-wins order guarantees these tags override any per-page
 * <meta name="robots"> or <link rel="canonical"> that a page-level
 * Helmet may have already written.
 */
const PROD_HOST = "thepropertic.ae";
const PROD_ORIGIN = `https://${PROD_HOST}`;

export default function HostGuard() {
  const { pathname, search } = useLocation();
  // Guard against SSR / unit-test environments where window is absent.
  const currentHost = typeof window === "undefined" ? PROD_HOST : window.location.hostname;
  const isProd = currentHost === PROD_HOST;
  if (isProd) return null;

  // Force every crawler to drop any preview URL and route trust to prod.
  const canonical = `${PROD_ORIGIN}${pathname}${search || ""}`;
  const robotsBlock = "noindex, nofollow, noarchive, nosnippet, notranslate, noimageindex";
  return (
    <Helmet>
      {/* Generic + major-engine specific noindex signals. Some engines
          honour a specific bot token even when the generic `robots` meta
          is overridden downstream. */}
      <meta name="robots" content={robotsBlock} />
      <meta name="googlebot" content={robotsBlock} />
      <meta name="bingbot" content={robotsBlock} />
      <meta name="yandex" content={robotsBlock} />
      <meta name="slurp" content={robotsBlock} />
      <meta name="duckduckbot" content={robotsBlock} />
      {/* AI crawlers — we don't want preview data feeding training or
          answer citations either. */}
      <meta name="gptbot" content={robotsBlock} />
      <meta name="oai-searchbot" content={robotsBlock} />
      <meta name="chatgpt-user" content={robotsBlock} />
      <meta name="claudebot" content={robotsBlock} />
      <meta name="perplexitybot" content={robotsBlock} />
      <meta name="google-extended" content={robotsBlock} />
      {/* Force canonical to the production origin so any preview URL
          already in a search index is consolidated to the prod URL on
          next crawl. */}
      <link rel="canonical" href={canonical} />
      {/* X-Robots-Tag equivalent for crawlers that read meta only. */}
      <meta name="referrer" content="no-referrer" />
    </Helmet>
  );
}
